/**
 * Karta subskrypcji — lista cyklicznych transakcji z toggle pause/resume.
 */
import { Icons } from '../Icons';
import { formatCurrency, safeColor, haptic } from '../../lib/utils';
import { getCategoryIcon } from '../../lib/categoryIcons';
import type { Subscription } from '../../features/analytics/selectors';
import { updateTransactionRemote } from '../../features/transactions/api';
import { showToast } from '../ui/Toast';

interface Props {
  subscriptions: Subscription[];
}

export function SubscriptionsCard({ subscriptions }: Props) {
  const total = subscriptions.filter((s) => !s.isPaused).reduce((s, x) => s + x.tx.amount, 0);

  const handleToggle = async (sub: Subscription) => {
    haptic('light');
    try {
      await updateTransactionRemote(sub.tx.id, { isPaused: !sub.isPaused });
      showToast(sub.isPaused ? 'Wznowiono subskrypcję' : 'Wstrzymano subskrypcję', 'info');
    } catch {
      showToast('Nie udało się zmienić subskrypcji', 'error');
    }
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-card border border-slate-100 dark:border-slate-700 shadow-card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-sky-100 dark:bg-sky-500/15 flex items-center justify-center">
            <Icons.Repeat size={18} className="text-sky-600 dark:text-sky-400" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-400 font-bold">Subskrypcje</p>
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
              {subscriptions.length} {subscriptions.length === 1 ? 'pozycja' : 'pozycji'}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wide text-slate-400 font-bold">Aktywne / mc</p>
          <p className="text-sm font-bold tabular-nums text-rose-600 dark:text-rose-300">{formatCurrency(total)}</p>
        </div>
      </div>

      {subscriptions.length === 0 ? (
        <p className="text-center text-sm text-slate-500 dark:text-slate-400 py-4">
          Brak cyklicznych transakcji
        </p>
      ) : (
        <ul className="space-y-1.5">
          {subscriptions.map((s) => {
            const Icon = getCategoryIcon(s.category?.icon);
            const color = safeColor(s.category?.color ?? '#94a3b8', '#94a3b8');
            return (
              <li key={s.tx.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                <span
                  className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${color}22`, color }}
                >
                  <Icon size={18} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${s.isPaused ? 'text-slate-400 line-through' : 'text-slate-800 dark:text-slate-100'}`}>
                    {s.tx.note || s.category?.name || 'Cykliczna'}
                  </p>
                  <p className="text-[11px] text-slate-400">{s.category?.name ?? '—'}</p>
                </div>
                <span className={`text-sm font-bold tabular-nums shrink-0 ${s.isPaused ? 'text-slate-400' : 'text-slate-900 dark:text-white'}`}>
                  {formatCurrency(s.tx.amount)}
                </span>
                <button
                  type="button"
                  onClick={() => handleToggle(s)}
                  aria-label={s.isPaused ? 'Wznów' : 'Wstrzymaj'}
                  className="w-9 h-9 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                >
                  {s.isPaused ? <Icons.Play size={16} /> : <Icons.Pause size={16} />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
